import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ParticleField, DataStream, MorphBlob, FlowLines, NodeNetwork } from "@/components/effects/DynamicBackgrounds";
import { SEOHead, buildBreadcrumbLD } from "@/components/seo/SEOHead";
import { 
  Upload, 
  Cpu, 
  BarChart3, 
  Zap, 
  Shield, 
  ArrowRight,
  FileText,
  Database,
  CheckCircle,
  Clock,
  Brain
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const steps = [
  {
    number: "01",
    icon: Upload,
    title: "Upload Your Data",
    description: "Simply upload your spend data (CSV, Excel) or contracts (PDF, DOCX). No complex integrations or IT support needed.",
    details: [
      "Drag & drop interface",
      "Supports all major file formats",
      "Secure, encrypted upload",
      "No data formatting required"
    ],
    color: "#00B4D8"
  },
  {
    number: "02",
    icon: Cpu,
    title: "AI Processes & Enriches",
    description: "Our AI engine automatically cleanses, classifies, and enriches your data using machine learning models trained on millions of transactions.",
    details: [
      "Automatic data cleansing",
      "AI-powered classification",
      "Supplier normalization",
      "Currency conversion"
    ],
    color: "#8B5CF6"
  },
  {
    number: "03",
    icon: BarChart3,
    title: "Generate Insights",
    description: "Within hours, receive comprehensive analytics, risk assessments, and AI-generated recommendations tailored to your business.",
    details: [
      "Spend analytics dashboards",
      "Contract risk scores",
      "Savings opportunities",
      "Executive summaries"
    ],
    color: "#10B981"
  },
  {
    number: "04",
    icon: Zap,
    title: "Take Action",
    description: "Use AI-driven recommendations to make faster, smarter procurement decisions. Every insight comes with clear next steps.",
    details: [
      "Prioritized action items",
      "One-click reporting",
      "Stakeholder presentations",
      "Implementation roadmaps"
    ],
    color: "#F59E0B"
  },
  {
    number: "05",
    icon: Shield,
    title: "Continuous Monitoring",
    description: "Set up alerts and automated reviews to maintain compliance, track performance, and never miss important deadlines.",
    details: [
      "Contract renewal alerts",
      "Spend anomaly detection",
      "Compliance monitoring",
      "Performance tracking"
    ],
    color: "#EF4444"
  }
];

const useCases = [
  {
    icon: Database,
    title: "Spend Visibility",
    scenario: "A CFO needs to understand company-wide spending patterns before the board meeting.",
    solution: "Upload 12 months of AP data → Same-day spend analysis with category breakdown, top suppliers, and savings opportunities ready to present.",
    timeframe: "4 hours"
  },
  {
    icon: FileText,
    title: "Contract Risk Review",
    scenario: "Legal team receives a 50-page supplier contract that needs review before signing.",
    solution: "Upload contract → AI identifies 12 risk clauses, auto-renewal terms, and liability caps. Executive summary with approval recommendation in hand.",
    timeframe: "20 seconds"
  },
  {
    icon: Brain,
    title: "Supplier Consolidation",
    scenario: "Procurement director suspects there are too many overlapping suppliers.",
    solution: "Upload supplier data → AI identifies 23 duplicate suppliers, recommends 8 consolidation opportunities with projected $450K annual savings.",
    timeframe: "2 hours"
  }
];

export const HowItWorksPage = () => {
  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="How It Works"
        description="See how ProcureAide transforms procurement in four steps: upload your data, let AI classify and enrich it, gain actionable intelligence, and drive measurable outcomes."
        path="/how-it-works"
        jsonLd={buildBreadcrumbLD([{ name: "Home", path: "/" }, { name: "How It Works", path: "/how-it-works" }])}
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={20} />
        <DataStream count={6} className="opacity-30" />
        <MorphBlob color="rgba(0,180,216,0.06)" size="500px" className="top-[10%] right-[-10%]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#00B4D8]/5 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              How It Works
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
            >
              From Data to Decisions in{" "}
              <span className="text-[#00B4D8]">Five Simple Steps</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-[#94A3B8]"
            >
              No complex integrations. No IT dependencies. Just upload, analyze, and act.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Timeline Steps */}
      <section className="py-20 relative overflow-hidden">
        <FlowLines count={4} className="opacity-20" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#00B4D8] via-[#8B5CF6] to-[#EF4444] transform md:-translate-x-1/2" />
            
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative flex flex-col md:flex-row items-start gap-8 mb-16 ${
                  index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                }`}
              >
                {/* Timeline dot */}
                <div 
                  className="absolute left-8 md:left-1/2 w-4 h-4 rounded-full transform -translate-x-1/2 z-10"
                  style={{ 
                    backgroundColor: step.color,
                    boxShadow: `0 0 20px ${step.color}80`
                  }}
                />

                {/* Content */}
                <div className={`md:w-1/2 pl-20 md:pl-0 ${index % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16"}`}>
                  <div 
                    className={`inline-block px-3 py-1 rounded-full text-xs font-bold mb-3`}
                    style={{ backgroundColor: `${step.color}20`, color: step.color }}
                  >
                    Step {step.number}
                  </div>
                  <h3 className="text-2xl font-bold text-[#F8FAFC] mb-3">{step.title}</h3>
                  <p className="text-[#94A3B8] mb-4">{step.description}</p>
                  <ul className={`space-y-2 ${index % 2 === 0 ? "md:ml-auto" : ""}`}>
                    {step.details.map((detail, i) => (
                      <li 
                        key={i} 
                        className={`flex items-center gap-2 text-sm text-[#64748B] ${
                          index % 2 === 0 ? "md:flex-row-reverse" : ""
                        }`}
                      >
                        <CheckCircle className="w-4 h-4" style={{ color: step.color }} />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Icon */}
                <div className="hidden md:flex md:w-1/2 items-center justify-center">
                  <div 
                    className="w-24 h-24 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${step.color}15` }}
                  >
                    <step.icon className="w-12 h-12" style={{ color: step.color }} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <NodeNetwork count={10} className="opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Real-World Use Cases
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              See how Procureaide transforms common procurement challenges into quick wins
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="feature-card p-6"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00B4D8]/10 flex items-center justify-center mb-4">
                  <useCase.icon className="w-6 h-6 text-[#00B4D8]" />
                </div>
                <h3 className="text-lg font-bold text-[#F8FAFC] mb-3">{useCase.title}</h3>
                
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">Scenario</p>
                  <p className="text-sm text-[#94A3B8]">{useCase.scenario}</p>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-wider text-[#00B4D8] mb-1">Solution</p>
                  <p className="text-sm text-[#F8FAFC]">{useCase.solution}</p>
                </div>
                
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-[#10B981]" />
                  <span className="text-[#10B981] font-medium">Time to insight: {useCase.timeframe}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              Ready to See It in Action?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8"
            >
              Preview our platform walkthrough or schedule a personalized demo with our team.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/#demo">
                <Button 
                  size="lg"
                  className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] px-8"
                  data-testid="how-it-works-demo-btn"
                >
                  Preview Platform
                </Button>
              </Link>
              <a href="mailto:info@procureaide.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10 px-8"
                  data-testid="how-it-works-contact-btn"
                >
                  Book a Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
